import * as React from "react";

/* ── Fallback career tips (always available) ── */

const FALLBACK_ITEMS = [
  { title: "How to tailor your resume for ATS systems in 2026", link: "https://blog.coursera.org", category: "Career" },
  { title: "Top 10 in-demand skills for European job seekers", link: "https://blog.coursera.org", category: "Skills" },
  { title: "The complete guide to acing remote job interviews", link: "https://blog.coursera.org", category: "Interview" },
  { title: "Why AI-powered applications are changing the hiring game", link: "https://blog.coursera.org", category: "AI" },
  { title: "Salary negotiation strategies that actually work", link: "https://blog.coursera.org", category: "Salary" },
  { title: "Building a standout LinkedIn profile: expert tips", link: "https://blog.coursera.org", category: "Branding" },
  { title: "Cover letter mistakes that cost you the interview", link: "https://blog.coursera.org", category: "Tips" },
  { title: "How to switch careers successfully in a competitive market", link: "https://blog.coursera.org", category: "Career" },
];

type FeedItem = { title: string; link: string; category: string };

/* ── Parse RSS XML ── */

function parseRssXml(xml: string): FeedItem[] {
  const items: FeedItem[] = [];
  const itemRegex = /<item>([\s\S]*?)<\/item>/g;
  let match: RegExpExecArray | null;

  while ((match = itemRegex.exec(xml)) !== null && items.length < 10) {
    const block = match[1];
    const title = block.match(/<title>([\s\S]*?)<\/title>/)?.[1]?.trim() ?? "";
    const link = block.match(/<link>([\s\S]*?)<\/link>/)?.[1]?.trim() ?? "";
    const category = block.match(/<category><!\[CDATA\[([\s\S]*?)\]\]><\/category>/)?.[1]?.trim() ?? "Career";

    if (title && link) {
      items.push({
        title: title.replace(/<!\[CDATA\[|\]\]>/g, ""),
        link,
        category,
      });
    }
  }

  return items;
}

/* ── Glassmorphism Ticker Pill ── */

export function RssTicker() {
  const [items, setItems] = React.useState<FeedItem[]>(FALLBACK_ITEMS);
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  // Try fetching live RSS feed client-side via CORS proxy, with sessionStorage cache
  React.useEffect(() => {
    const controller = new AbortController();
    const CACHE_KEY = "rss-ticker-data";
    const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

    // Check sessionStorage cache first
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { items: cachedItems, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL && cachedItems?.length > 0) {
          setItems(cachedItems);
          return;
        }
      }
    } catch {
      // Invalid cache, continue to fetch
    }

    (async () => {
      try {
        const proxyUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent("https://blog.coursera.org/feed/")}`;
        const res = await fetch(proxyUrl, { signal: controller.signal });
        if (!res.ok) return;
        const xml = await res.text();

        if (xml) {
          const parsed = parseRssXml(xml);
          if (parsed.length > 0) {
            setItems(parsed);
            try {
              sessionStorage.setItem(CACHE_KEY, JSON.stringify({ items: parsed, ts: Date.now() }));
            } catch {
              // sessionStorage full or unavailable
            }
          }
        }
      } catch {
        // Keep fallback items
      }
    })();

    return () => controller.abort();
  }, []);

  // Auto-rotate headlines every 4.5s
  React.useEffect(() => {
    if (items.length <= 1) return;
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setActiveIndex((prev) => (prev + 1) % items.length);
        setIsTransitioning(false);
      }, 300);
    }, 4500);
    return () => clearInterval(interval);
  }, [items.length]);

  const item = items[activeIndex];
  if (!item) return null;

  return (
    <a
      href={item.link}
      target="_blank"
      rel="noopener noreferrer"
      className="ticker-pill"
      aria-label="Career advice from Coursera"
    >
      {/* Pulsing dot */}
      <span className="ticker-dot" />

      {/* Category */}
      <span className="ticker-cat">{item.category}</span>

      {/* Divider */}
      <span className="ticker-divider" />

      {/* Headline with fade transition */}
      <span
        className="ticker-headline"
        style={{
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? "translateY(6px)" : "translateY(0)",
        }}
      >
        {item.title}
      </span>

      {/* Arrow */}
      <svg className="ticker-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 17l9.2-9.2M17 17V7.8H7.8" />
      </svg>
    </a>
  );
}
